import { DROP_CONFIG } from 'components/Drops/dropConfig';
import { useMemo } from 'react';
import useStakingContracts from './useStakingContracts';
import { Polling } from 'components/common';
import { useWallet } from 'components/WalletContext';
import useBalances from './useBalances';
import { ethers } from 'ethers';
import { useQuery } from 'react-query';
import { STAKE_CONFIG } from 'components/Staking/stakeConfig';

const useStaking = () => {
  const {
    contractsReady,
    getAllPairs,
    getStakedBalanceOfBatch,
    getTotalStaked,
    getPendingRewards,
    claimReward,
    getMaxPairs,
  } = useStakingContracts();
  const { address } = useWallet();
  const { balances } = useBalances('all', address, Polling.average);
  const { balances: stakingContractBalances } = useBalances(
    'all',
    STAKE_CONFIG.contractAddress,
    Polling.average
  );

  const { data: pairs } = useQuery(
    ['pairs'],
    async () => {
      const pairs = await getAllPairs();
      return pairs.filter(p => p.enabled);
    },
    {
      enabled: contractsReady,
    }
  );

  const { data: stakedBalances } = useQuery(
    ['stakedBalances', address],
    async () => {
      if (!address) {
        console.log('[fetching stakedBalances] address not initialized');
        return '';
      }

      const genesisIds = pairs!.map(p => p.genesisId);
      if (genesisIds.length === 0) return;

      // note: this assumes each track per LP will only be in a single pair
      const genesisBalances = await getStakedBalanceOfBatch(
        address!,
        genesisIds.map(() => DROP_CONFIG.genesis.contractAddress),
        genesisIds
      );

      return genesisBalances.map(b => b.toNumber());
    },
    {
      refetchInterval: Polling.average,
      enabled: contractsReady && Boolean(address) && Boolean(pairs) && pairs!.length > 0,
    }
  );

  const pairsWithBalances = useMemo(() => {
    if (!stakedBalances || !pairs || pairs.length !== stakedBalances.length) return [];

    return pairs.map((p, index) => {
      // user balances
      const enigmaBalance = balances[p.enigmaId] || 0;
      const genesisBalance = balances[p.genesisId] || 0;
      const pairBalance = Math.min(enigmaBalance, genesisBalance);

      // contract balances (staked)
      const stakedEnigmaBalance = stakingContractBalances[p.enigmaId] || 0;
      const stakedGenesisBalance = stakingContractBalances[p.genesisId] || 0;
      const stakedPairBalance = Math.min(stakedEnigmaBalance, stakedGenesisBalance);

      return {
        ...p,
        balance: {
          enigma: enigmaBalance,
          genesis: genesisBalance,
          pair: pairBalance,
          staked: stakedBalances[index],
          totalStaked: stakedPairBalance,
        },
      };
    });
  }, [pairs, balances, stakedBalances, stakingContractBalances]);

  const { data: totalStaked } = useQuery(
    ['totalStaked'],
    async () => {
      if (!address) {
        console.log('[fetching totalStaked] address not initialized');
        return 0;
      }

      const staked = await getTotalStaked();
      return staked.toNumber();
    },
    {
      refetchInterval: Polling.average,
      enabled: contractsReady && Boolean(address),
    }
  );

  const { data: maxPairs } = useQuery(
    ['maxPairs'],
    async () => {
      if (!address) {
        console.log('[fetching maxPairs] address not initialized');
        return 0;
      }

      const max = await getMaxPairs();
      return max.toNumber();
    },
    {
      enabled: contractsReady && Boolean(address),
    }
  );

  const { data: pendingRewards } = useQuery(
    ['pendingRewards', address],
    async () => {
      if (!address) {
        console.log('[fetching pendingRewards] address not initialized');
        return '';
      }

      const rewards = await getPendingRewards(address!);
      return ethers.utils.formatEther(rewards);
    },
    {
      refetchInterval: Polling.average,
      enabled: contractsReady && Boolean(address),
    }
  );

  const portfolioPairs = useMemo(() => {
    return pairsWithBalances.filter(p => {
      return p.balance.pair > 0 || p.balance.staked > 0;
    });
  }, [pairsWithBalances]);

  const unownedPairs = useMemo(() => {
    const portfolioPairIds = portfolioPairs.map(p => p.pairId);
    return pairsWithBalances.filter(p => !portfolioPairIds.includes(p.pairId));
  }, [pairsWithBalances, portfolioPairs]);

  return {
    pairs: pairs || [],
    stakedBalances,
    pairsWithBalances,
    totalStaked: totalStaked || 0,
    pendingRewards: pendingRewards || '',
    claimReward,
    portfolioPairs,
    unownedPairs,
    maxPairs,
  };
};

export default useStaking;
